import { toast } from 'sonner';

/**
 * Detects when this running bundle is older than the one deployed, and gets
 * the user onto the new one.
 *
 * Why this exists: an iPhone Safari tab opened before a deploy keeps
 * executing the OLD JavaScript for hours — Safari resurrects tabs from
 * memory without revalidating, and Vite's content-hashed chunks from the
 * previous deployment stay fetchable on Vercel, so nothing ever breaks to
 * force a reload. Seen live on 2026-08-25: a fix was deployed at 09:46 UTC
 * and the owner's phone still reproduced the fixed bug at 15:39 UTC; a hard
 * refresh cured it. The `vite:preloadError` listener in main.tsx only
 * catches the case where an old chunk 404s — it cannot catch a stale bundle
 * that still loads fine.
 *
 * Two behaviours, chosen by how the staleness is discovered:
 * - The tab RETURNS TO LIFE (pageshow from the back-forward cache, or the
 *   document becoming visible again): reload immediately. The user was not
 *   mid-interaction — this is exactly the resurrected-phone-tab case — and
 *   booking drafts persist to localStorage, so the flow resumes where it
 *   was. A sessionStorage marker caps this at ONE automatic reload per
 *   remote sha, so alias/CDN lag cannot cause a reload loop.
 * - A periodic check fires DURING active use: never yank the page from
 *   under someone typing. Offer a toast with a Refresh action instead.
 */

const OWN_SHA: string = import.meta.env.VITE_BUILD_SHA ?? 'dev';
/** At most one network check per this window, whatever the trigger spam. */
const MIN_CHECK_GAP_MS = 60_000;
const PERIODIC_MS = 5 * 60_000;
const RELOADED_KEY = 'version-watchdog-reloaded-for';

let lastCheckAt = 0;
let toastShown = false;

async function fetchDeployedSha(): Promise<string | null> {
  try {
    // Served with Cache-Control: no-store (vercel.json), so this answer is
    // always the edge's current deployment, never a cached one.
    const res = await fetch('/build-info.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const info: unknown = await res.json();
    const sha = (info as { sha?: unknown })?.sha;
    return typeof sha === 'string' && sha.length > 0 && sha !== 'unknown' ? sha : null;
  } catch {
    // Offline or transient failure — staleness is a nicety, never an error.
    return null;
  }
}

export type WatchdogTrigger = 'resume' | 'interval';

/** Exported for tests. Returns what it did: 'fresh' | 'reloaded' | 'toast' | 'skipped'. */
export async function checkVersion(trigger: WatchdogTrigger): Promise<string> {
  const now = Date.now();
  if (now - lastCheckAt < MIN_CHECK_GAP_MS) return 'skipped';
  lastCheckAt = now;

  const deployed = await fetchDeployedSha();
  if (!deployed || deployed === OWN_SHA) return 'fresh';

  if (trigger === 'resume' && sessionStorage.getItem(RELOADED_KEY) !== deployed) {
    // One shot per sha: if the alias still serves the old bundle after this
    // reload (CDN lag), we fall through to the toast path next time rather
    // than reloading forever.
    try { sessionStorage.setItem(RELOADED_KEY, deployed); } catch { /* quota/private mode */ }
    window.location.reload();
    return 'reloaded';
  }

  if (!toastShown) {
    toastShown = true;
    toast('A new version of the app is available', {
      description: 'Refresh to get the latest fixes.',
      duration: Infinity,
      action: { label: 'Refresh', onClick: () => window.location.reload() },
      onDismiss: () => { toastShown = false; },
    });
  }
  return 'toast';
}

export function startVersionWatchdog(): void {
  // Local dev has no deployment to compare against.
  if (OWN_SHA === 'dev') return;

  window.addEventListener('pageshow', (e: PageTransitionEvent) => {
    // Only bfcache restores — a normal load IS the fresh bundle already.
    if (e.persisted) void checkVersion('resume');
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkVersion('resume');
  });
  window.setInterval(() => void checkVersion('interval'), PERIODIC_MS);
}
